import { useEffect, useState } from "react";
import InputField from "../../common/InputField";
import { MdClose } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import Button from "../../common/Button";
import { IoMdClose } from "react-icons/io";
import { FaPaperPlane } from "react-icons/fa";
import { unitConversionAPI } from "../../utlis/apiService";

const PackingForm = ({
  initialData,
  onSave,
  onCancel,
  isEditing,
  setFormData,
  formData,
  isSaving = false,
}) => {
  const today = new Date().toISOString()?.split("T")[0];
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [isUnitConvData, setIsUnitConvData] = useState([]);

  useEffect(() => {
    const fetchUnitConvData = async () => {
      try {
        const res = await unitConversionAPI.getAll();
        setIsUnitConvData(
          res.data.map((u) => ({
            value: u.unit_Conv_Id,
            label: u.unit_Conv_Name,
            raw: u.conv_Value,
          })),
        );
      } catch (err) {
        console.error("Error loading dropdowns", err);
      }
    };
    fetchUnitConvData();
  }, []);

  useEffect(() => {
    if (initialData) {
      const updatedForm = {
        bundle_No: initialData.bundle_No || "0",
        packing_Date: initialData.packing_Date
          ? initialData.packing_Date.split("T")[0]
          : today,
        order_No: initialData.order_No || "",
        dieNumber: initialData.dieNumber || "",
        die_Id: initialData.die_Id ?? null,
        batch_Id: initialData.batch_Id || "",
        casting_No: initialData.casting_No || "",
        cut_Length: initialData.cut_Length || "",
        wt_12_FT: initialData.wT_12_FT || "",
        wt_Cut_Length: initialData.wT_Cut_Length || "",
        pcs: initialData.pcs || "",
        total_Weight: initialData.total_Weight || "",
        series_Name: initialData.series_Name || "",
        remark: initialData.remark || "",
        unit_Conv_Id: initialData.unit_Conv_Id || "",
        final_CutLength_weight: initialData.final_CutLength_weight || "",
      };
      setFormData(updatedForm);
    }
  }, [initialData]);

  const calculateWt12FT = (pcs, totalWeight, cutLength) => {
    if (pcs && totalWeight && cutLength) {
      const result = ((totalWeight / pcs) * 12) / cutLength;
      return isNaN(result) ? "" : result.toFixed(2);
    }
    return "";
  };

  const handleChange = (e, field) => {
    const value = typeof e === "object" && e?.target ? e.target.value : e;
    const updatedForm = { ...formData, [field]: value };

    const pcs = parseFloat(field === "pcs" ? value : formData.pcs || 0);
    const totalWeight = parseFloat(
      field === "total_Weight" ? value : formData.total_Weight || 0,
    );
    const cutLength = parseFloat(
      field === "cut_Length" ? value : formData.cut_Length || 0,
    );

    updatedForm.wt_12_FT = calculateWt12FT(pcs, totalWeight, cutLength);

    const selectedUnit = isUnitConvData.find(
      (u) =>
        u.value === (field === "unit_Conv_Id" ? value : formData.unit_Conv_Id),
    );
    const convValue = selectedUnit?.raw || 1;

    const wt12FT = parseFloat(updatedForm.wt_12_FT || 0);
    const wtCutLength = parseFloat(
      field === "wt_Cut_Length" ? value : formData.wt_Cut_Length || 0,
    );

    updatedForm.final_CutLength_weight =
      wt12FT && wtCutLength && convValue
        ? ((wt12FT * wtCutLength) / convValue).toFixed(2)
        : "";

    setFormData(updatedForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, formData.bundle_No);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
        >
          <div className="bg-gradient-to-r from-primary to-blue-700 text-white px-6 py-4 flex justify-between items-center border-b border-slate-700">
            <div>
              <h2 className="text-white text-xl font-bold tracking-tight uppercase">
                {isEditing ? "Edit Packing Entry" : "New Packing Entry"}
              </h2>
              <p className="text-slate-400 text-xs mt-1">
                Fill in the details to update bundle information.
              </p>
            </div>
            <button
              onClick={onCancel}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <MdClose size={24} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 bg-slate-50/30"
          >
            <h3 className="text-sm font-bold text-slate-500 uppercase mb-4 flex items-center gap-2">
              <span className="w-1 h-4 bg-primary rounded-full"></span> Basic
              Order Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <InputField
                label="Bundle No"
                value={formData.bundle_No}
                disabled
                className="bg-gray-100"
              />
              <InputField label="Batch ID" value={formData.batch_Id} disabled />
              <InputField
                label="Casting No"
                value={formData.casting_No}
                disabled
              />
              <InputField label="Order No" value={formData.order_No} disabled />
              <InputField
                label="Die Number"
                value={formData.dieNumber}
                disabled
              />
              <InputField
                label="Series Name"
                value={formData.series_Name}
                disabled
              />
              <InputField
                label="Cut Length"
                value={formData.cut_Length}
                disabled
              />
              <InputField
                type="date"
                label="Packing Date"
                value={formData.packing_Date}
                onChange={(val) => handleChange(val, "packing_Date")}
              />
            </div>

            <div className="mb-6">
              <h3 className="text-sm font-bold text-slate-500 uppercase mb-4 flex items-center gap-2">
                <span className="w-1 h-4 bg-primary rounded-full"></span>
                Measurements & Packing
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-blue-50/50 p-4 rounded-xl border border-blue-100">
                <div className="space-y-4 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    label="Total Weight"
                    value={formData.total_Weight}
                    onChange={(val) => handleChange(val, "total_Weight")}
                    className="focus:ring-2 focus:ring-orange/50"
                    unit="Kg"
                  />
                  <InputField
                    label="PCS"
                    value={formData.pcs}
                    onChange={(val) => handleChange(val, "pcs")}
                    unit="pcs"
                  />
                  <InputField
                    label="WT/12 FT (Auto)"
                    value={formData.wt_12_FT}
                    disabled
                    className="bg-blue-50 font-semibold"
                  />
                  <InputField
                    label="Final Cut Length"
                    value={formData.wt_Cut_Length}
                    onChange={(val) => handleChange(val, "wt_Cut_Length")}
                  />
                  <InputField
                    type="select"
                    label="Unit"
                    options={isUnitConvData}
                    value={formData.unit_Conv_Id}
                    onChange={(val) => handleChange(val, "unit_Conv_Id")}
                  />
                  <InputField
                    label="Calculated Weight (Auto)"
                    value={formData.final_CutLength_weight}
                    disabled
                    className="bg-green-50 font-semibold text-green-700"
                  />
                </div>

                <div className="flex flex-col">
                  <label className="text-sm font-medium text-slate-700 mb-1">
                    Remarks
                  </label>
                  <textarea
                    className="w-full h-full min-h-[120px] p-3 border border-slate-300 rounded-md focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                    placeholder="Enter any additional notes..."
                    value={formData.remark}
                    onChange={(e) => handleChange(e.target.value, "remark")}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end items-center space-x-4 pt-6 border-t mt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-6 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-red-50 hover:border-red-500 transition-all flex items-center gap-2"
              >
                <IoMdClose size={20} />
                Cancel
              </button>

              <Button
                type="submit"
                className="px-8 py-2.5 rounded-lg bg-orange hover:bg-orange text-white font-bold shadow-lg shadow-orange-200 transition-all flex items-center gap-2"
                icon={FaPaperPlane}
                loading={isSaving}
                loadingText="Saving..."
                text="Save Entry"
              />
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PackingForm;
